// covered-by: frontends/carrie/src/ask-v2/shell/__tests__/AskV2SidebarRecents.test.tsx
/**
 * Shared Recents row parts for BOTH sidebars.
 *
 * `AppSidebar` (V1) and `AskV2SidebarRecents` (V2) render the same Recents
 * list, and these three helpers used to exist as byte-identical copies in each
 * file — so every icon/meta tweak had to be made twice and the two rails drifted
 * the first time one copy was missed (the sr-only kind label was already a
 * two-file edit for exactly that reason). One module, two importers.
 */
import { type AskPunttProjectReviewState } from "@mg/schemas/src/christo/askPunttProject";
import { Chat, Kanban } from "@phosphor-icons/react";
import cx from "classnames";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";

import {
  SIDEBAR_ITEM_META_CLASS,
  SIDEBAR_ITEM_META_STYLE,
} from "./sidebarItemStyles";

// Per-state label + dot color for the project review status line. Only ACTIVE
// review states earn a dot/label in the rail; settled outcomes
// (changes_requested, approved) render nothing so the Recents list is not a
// wall of repeated status noise (product ask: only surface active statuses like
// "In review").
const REVIEW_STATE_PRESENTATION: Record<
  AskPunttProjectReviewState,
  { label: string; dotClass: string } | null
> = {
  in_review: { label: "", dotClass: "bg-puntt-accent-9" }, // label filled at render via t("nav.inReview")
  changes_requested: null,
  approved: null,
  none: null,
};

// why: Phosphor Kanban + square Chat — nav KanbanIcon/ChatIcon fills assume a white row, and that chat glyph is two bubbles
export function RecentsKindIcon({
  kind,
  id,
}: {
  kind: "project" | "chat";
  id: string;
}) {
  const { t } = useTranslation("common");
  const Icon = kind === "project" ? Kanban : Chat;
  return (
    <>
      <span
        className="shrink-0 text-puntt-neutral-gray-11"
        aria-hidden
        data-testid={`sidebar-ask-puntt-recent-kind-${id}`}
      >
        <Icon size={16} color="currentColor" />
      </span>
      {/* why: the glyph is the only kind cue left in the row, and it is aria-hidden —
          this keeps project/chat in the link's accessible name (WCAG 1.1.1).
          sr-only is position:absolute, so it is not a flex item and adds no gap. */}
      <span
        className="sr-only"
        data-testid={`sidebar-ask-puntt-recent-kind-label-${id}`}
      >
        {kind === "project" ? t("nav.project") : t("nav.chat")}
      </span>
    </>
  );
}

export function ProjectRecentMeta({
  state,
}: {
  state?: AskPunttProjectReviewState;
}) {
  const { t } = useTranslation("common");
  const presentation = state ? REVIEW_STATE_PRESENTATION[state] : null;
  if (!presentation) return null;
  return (
    <span
      className={SIDEBAR_ITEM_META_CLASS}
      style={SIDEBAR_ITEM_META_STYLE}
      data-testid="sidebar-ask-puntt-recent-meta"
    >
      <span
        className="flex items-center gap-1.5 truncate"
        data-testid="sidebar-ask-puntt-project-status"
      >
        <span
          aria-hidden
          className={cx(
            "size-1.5 shrink-0 rounded-full",
            presentation.dotClass,
          )}
        />
        <span className="truncate">{t("nav.inReview")}</span>
      </span>
    </span>
  );
}

/** "Owner · 5 minutes ago" for a chat row; empty string when neither part exists. */
export function formatChatMeta(
  updatedAt?: string | null,
  ownerName?: string | null,
): string {
  let when: string | null = null;
  if (updatedAt) {
    const date = new Date(updatedAt);
    if (!Number.isNaN(date.getTime())) {
      when = formatDistanceToNow(date, { addSuffix: true });
    }
  }
  const parts = [ownerName?.trim() || null, when].filter(
    (part): part is string => Boolean(part),
  );
  return parts.join(" · ");
}
